import fetchWithAuth from "@/api/secureFetch";

export type RepositoryImportStatus =
    | "queued"
    | "cloning"
    | "processing"
    | "completed"
    | "failed"
    | "cancelled";

export interface RepositoryImportJob {
    jobId: string;
    repositoryId: number;
    repositoryName: string;
    provider: string;
    branch: string | null;
    status: RepositoryImportStatus;
    stage: string | null;
    progress: number;
    filesCompleted: number | null;
    filesTotal: number | null;
    message: string | null;
    retryable: boolean;
    error?: { code: string; message: string };
    createdAt: string;
    updatedAt: string;
}

export interface RepositoryImportsResponse {
    draftId: number;
    currentDraftVersion: number;
    imports: RepositoryImportJob[];
}

export interface RepositoryImportSelection {
    repositoryId: number;
    branch?: string | null;
}

export class RepositoryImportsApiError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(status: number, code: string, message: string) {
        super(message);
        this.name = "RepositoryImportsApiError";
        this.status = status;
        this.code = code;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJob(value: unknown): RepositoryImportJob {
    if (!isRecord(value) || typeof value.jobId !== "string" ||
        typeof value.repositoryId !== "number" || typeof value.repositoryName !== "string" ||
        typeof value.provider !== "string" || typeof value.status !== "string") {
        throw new Error("Invalid repository import response");
    }
    const statuses: RepositoryImportStatus[] = ["queued", "cloning", "processing", "completed", "failed", "cancelled"];
    if (!statuses.includes(value.status as RepositoryImportStatus)) {
        throw new Error("Invalid repository import status");
    }
    const progress = typeof value.progress === "number" && Number.isFinite(value.progress)
        ? Math.max(0, Math.min(100, value.progress))
        : 0;
    return {
        jobId: value.jobId,
        repositoryId: value.repositoryId,
        repositoryName: value.repositoryName,
        provider: value.provider,
        branch: typeof value.branch === "string" ? value.branch : null,
        status: value.status as RepositoryImportStatus,
        stage: typeof value.stage === "string" ? value.stage : null,
        progress,
        filesCompleted: typeof value.filesCompleted === "number" ? value.filesCompleted : null,
        filesTotal: typeof value.filesTotal === "number" ? value.filesTotal : null,
        message: typeof value.message === "string" ? value.message : null,
        retryable: value.retryable === true,
        error: isRecord(value.error) && typeof value.error.message === "string"
            ? {
                code: typeof value.error.code === "string" ? value.error.code : "IMPORT_FAILED",
                message: value.error.message
            }
            : undefined,
        createdAt: typeof value.createdAt === "string" ? value.createdAt : "",
        updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : ""
    };
}

async function readResponse(response: Response): Promise<RepositoryImportsResponse> {
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new RepositoryImportsApiError(response.status || 503, "INVALID_RESPONSE", "Repository import returned an invalid response.");
    }
    if (!response.ok) {
        const error = isRecord(payload) && isRecord(payload.error) ? payload.error : payload;
        const stringError = isRecord(payload) && typeof payload.error === "string" ? payload.error : null;
        throw new RepositoryImportsApiError(
            response.status,
            isRecord(error) && typeof error.code === "string" ? error.code : "IMPORT_UNAVAILABLE",
            isRecord(error) && typeof error.message === "string"
                ? error.message
                : stringError ?? "Repository import is temporarily unavailable."
        );
    }
    if (!isRecord(payload) || typeof payload.draftId !== "number" ||
        typeof payload.currentDraftVersion !== "number" || !Array.isArray(payload.imports)) {
        throw new RepositoryImportsApiError(503, "INVALID_RESPONSE", "Repository import returned an invalid response.");
    }
    return {
        draftId: payload.draftId,
        currentDraftVersion: payload.currentDraftVersion,
        imports: payload.imports.map(parseJob)
    };
}

function basePath(draftId: number): string {
    return `/api/product-creation-draft/${draftId}/repository-imports`;
}

export async function scheduleRepositoryImports(
    token: string,
    draftId: number,
    draftVersion: number,
    repositories: RepositoryImportSelection[]
): Promise<RepositoryImportsResponse> {
    return readResponse(await fetchWithAuth(basePath(draftId), token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draftVersion, repositories })
    }));
}

export async function getRepositoryImports(token: string, draftId: number): Promise<RepositoryImportsResponse> {
    return readResponse(await fetchWithAuth(basePath(draftId), token, { headers: { Accept: "application/json" } }));
}

async function mutateJob(
    token: string,
    draftId: number,
    jobId: string,
    action: "retry" | "cancel"
): Promise<RepositoryImportJob> {
    const response = await fetchWithAuth(`${basePath(draftId)}/${encodeURIComponent(jobId)}/${action}`, token, {
        method: "POST"
    });
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new RepositoryImportsApiError(response.status || 503, "INVALID_RESPONSE", "Repository import returned an invalid response.");
    }
    if (!response.ok) {
        const error = isRecord(payload) && isRecord(payload.error) ? payload.error : payload;
        const stringError = isRecord(payload) && typeof payload.error === "string" ? payload.error : null;
        throw new RepositoryImportsApiError(
            response.status,
            isRecord(error) && typeof error.code === "string" ? error.code : "IMPORT_UNAVAILABLE",
            isRecord(error) && typeof error.message === "string"
                ? error.message
                : stringError ?? `Repository ${action} failed.`
        );
    }
    if (!isRecord(payload) || !("import" in payload)) {
        throw new RepositoryImportsApiError(503, "INVALID_RESPONSE", "Repository import returned an invalid response.");
    }
    return parseJob(payload.import);
}

export const retryRepositoryImport = (token: string, draftId: number, jobId: string) =>
    mutateJob(token, draftId, jobId, "retry");

export const cancelRepositoryImport = (token: string, draftId: number, jobId: string) =>
    mutateJob(token, draftId, jobId, "cancel");

export function repositoryImportIsActive(status: RepositoryImportStatus): boolean {
    return status === "queued" || status === "cloning" || status === "processing";
}
